import React, { useEffect, useMemo, useState } from 'react';
import { Bubble, Button, Checkbox, Dropdown, List, Radio } from 'tea-component';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { useIntl, getLocale } from 'umi';
import { cloneDeep } from 'lodash-es';
import './index.scss';
import { deepCompare } from '../../_config/utils';

interface RegionSelectProps {
  options: any[];
  value: string[];
  onChange: (value: string[]) => void;
  disabledWoChina?: boolean;
}

const RegionSelect = (props: RegionSelectProps) => {
  const { options, value, onChange, disabledWoChina = false } = props;
  const [radioValue, setRadioValue] = useState('global_wo_china');
  const [changeValue, setChangeValue] = useState([]);
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [isSelectAll, setIsSelectAll] = useState(true);

  const [currentValue, setCurrentValue] = useState<string[]>([]);

  useEffect(() => {
    if (value?.length === 1 && value[0] === 'global') {
      setRadioValue('global');
    }
    if (value?.length === 1 && value[0] === 'global_wo_china') {
      setRadioValue('global_wo_china');
    }
    setCurrentValue(value);
  }, [value]);

  const getRegionTip = (list: any[]) => {
    if (!Array.isArray(list)) return '';
    return list
      ?.map((item) =>
        locale === 'zh-CN'
          ? item?.country_name_ch
          : item?.country_name_en.replace(/,/g, ' '),
      )
      .join(',');
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      const list = options?.map((item) => item?.area_code);
      setCurrentValue(list);
      setRadioValue('');
    } else {
      setCurrentValue(['global']);
      setRadioValue('global');
    }
    setIsSelectAll(!isSelectAll);
  };

  const handleRegionChange = (value: boolean, code: string) => {
    if (value) {
      const list = cloneDeep(currentValue);

      if (
        list?.length === 1 &&
        ['global', 'global_wo_china']?.includes(list[0])
      ) {
        setCurrentValue([code]);
      } else {
        list.push(code);
        setCurrentValue(list);
      }
      setIsSelectAll(false);
      setRadioValue('');
    } else {
      const list = cloneDeep(currentValue);
      const index = list.findIndex((item) => item === code);
      if (index > -1) {
        list.splice(index, 1);
        if (!list.length) {
          setCurrentValue(['global']);
          setRadioValue('global');
          setIsSelectAll(true);
        } else {
          setCurrentValue(list);
        }
      }
    }
  };

  const titleNow = useMemo(() => {
    if (currentValue?.length === 1 && currentValue[0] === 'global') {
      return formatMessage({ id: 'dbi_global' });
    }

    if (currentValue?.length === 1 && currentValue[0] === 'global_wo_china') {
      return formatMessage({ id: 'dbi_global_wo_china' });
    }

    if (currentValue?.length === 1) {
      const region = options.find((item) => item.area_code === currentValue[0]);
      return locale === 'zh-CN' ? region?.area_name_ch : region?.area_name_en;
    }

    return currentValue?.length;
  }, [currentValue, radioValue, locale]);

  const handleOpenChange = (val: boolean) => {
    if (!val && value) {
      // 浅比较
      if (value?.length !== currentValue?.length) {
        onChange && onChange(currentValue);
        return;
      }
      // 深比较
      const list1 = value?.sort(
        (a: any, b: any) => a?.charCodeAt() - b?.charCodeAt(),
      );
      const list2 = currentValue?.sort(
        (a: any, b: any) => a?.charCodeAt() - b?.charCodeAt(),
      );
      const result = deepCompare(list1, list2);
      if (!result) {
        onChange && onChange(currentValue);
      }
    }
  };

  return (
    <Dropdown
      button={
        <div className="dbi-select-button">
          <span className="select-button-text">
            {formatMessage({ id: 'dbi_area_region' })}
          </span>
          <div className="select-button-value">{titleNow}</div>
        </div>
      }
      className="dbi-region-select"
      appearance="button"
      matchButtonWidth
      overlayClassName="dbi-region-select-overlay"
      clickClose={false}
      onOpenChange={(value) => handleOpenChange(value)}
    >
      {(close) => (
        <List type="option-group">
          <List.GroupLabel>
            {formatMessage({ id: 'dbi_benchmark_overall' })}
          </List.GroupLabel>
          <List.Item>
            <Radio
              name="global"
              value={radioValue == 'global'}
              onChange={() => {
                setRadioValue('global');
                setCurrentValue(['global']);
              }}
            >
              {formatMessage({ id: 'dbi_global' })}
              <TipContext
                className="dbi-region-overlay-tip"
                Performance="right"
                tipText={formatMessage({ id: 'dbi_info_global_filter' })}
              />
            </Radio>
          </List.Item>
          <List.Item>
            <Radio
              name="global_wo_china"
              disabled={disabledWoChina}
              value={radioValue == 'global_wo_china'}
              onChange={() => {
                setRadioValue('global_wo_china');
                setCurrentValue(['global_wo_china']);
              }}
            >
              {formatMessage({ id: 'dbi_global_wo_china' })}
            </Radio>
          </List.Item>
          <List.GroupLabel>
            <div className="dbi-region-overlay-all">
              {formatMessage({ id: 'dbi_area_region' })}
              <Button type="link" onClick={handleSelectAll}>
                {formatMessage({
                  id: isSelectAll ? 'dbi_select_all' : 'dbi_clear_all',
                })}
              </Button>
            </div>
          </List.GroupLabel>
          {options?.map((item, index) => {
            return (
              <List.Item key={index}>
                <Checkbox
                  name={item?.area_code}
                  onChange={(value) =>
                    handleRegionChange(value, item?.area_code)
                  }
                  value={!!currentValue?.includes(item?.area_code)}
                >
                  <Bubble
                    dark
                    placement="right"
                    style={{ maxWidth: 340 }}
                    content={
                      <div style={{ wordBreak: 'break-all' }}>
                        {getRegionTip(item?.country_set || [])}
                      </div>
                    }
                  >
                    <div className="dbi-region-overlay-text">
                      {locale === 'zh-CN'
                        ? item?.area_name_ch
                        : item?.area_name_en}
                      {`(${item?.country_set?.length || 0})`}
                    </div>
                  </Bubble>
                </Checkbox>
              </List.Item>
            );
          })}
        </List>
      )}
    </Dropdown>
  );
};

export default RegionSelect;
